import { Lock } from 'lucide-react';
import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';

import { cn } from '@/lib/cn';

import {
  isStageUnlocked,
  LOCK_HINTS,
  NAV_ITEMS,
  type NavItem,
  type WorkflowStage,
} from './nav-items';

type Props = {
  currentStage: WorkflowStage;
  /** Filter — some items are PM-only, some require componentization flag. */
  canSeePmOnly: boolean;
  hasComponentization: boolean;
};

export function SidebarNav({ currentStage, canSeePmOnly, hasComponentization }: Props) {
  const items = NAV_ITEMS.filter((item) => {
    if (item.pmOnly && !canSeePmOnly) return false;
    if (item.requiresComponentization && !hasComponentization) return false;
    return true;
  });

  return (
    <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4" aria-label="Primary">
      {items.map((item) => (
        <Fragment key={item.to}>
          <NavRow item={item} unlocked={isStageUnlocked(currentStage, item.minStage)} />
          {item.dividerAfter && <div className="my-2 mx-4 border-t border-gray-100" aria-hidden="true" />}
        </Fragment>
      ))}
    </nav>
  );
}

type NavRowProps = {
  item: NavItem;
  unlocked: boolean;
};

function NavRow({ item, unlocked }: NavRowProps) {
  const Icon = item.icon;

  if (unlocked) {
    return (
      <NavLink
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive
              ? 'bg-indigo-50 text-indigo-600'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
          )
        }
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
        <span>{item.label}</span>
      </NavLink>
    );
  }

  return (
    <div
      className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-300"
      title={LOCK_HINTS[item.minStage] || 'Locked'}
      aria-disabled="true"
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
      <span className="flex-1">{item.label}</span>
      <Lock className="h-3.5 w-3.5" aria-label="Locked" />
    </div>
  );
}
